import { runInAction } from 'mobx';
import { sendSizingAppliedEventForGroups } from '../../lib/fragment-helper';
import presizeBackend from '../../services/presize-backend';
import presizeBackendRest from '../../services/presize-backend-rest';
import { isDbMigrated } from '../../lib/rest-feature-flag';
import { SizingStore } from './sizing-store';
import { DistributionType } from '../../models/sizing';
import { message } from '@retail-core/rds';
import { ApplySizingGroupInput } from '../../models/request';
import { ApplySizingBulkResponse, ApplySizingBulkResponseUnion, ApplySizingGroupResponse } from '../../models/response';
import { getPreselectedSizeChartId } from '../../lib/article-utils';
import { sendEvent } from '../../services/event-tracking';
import { SizingGroup } from '../../models/sizing-group';

export class ApplyReferenceBrandAction {
  async bulkApplyReferenceBrand(this: SizingStore, referenceBrandCode?: string | null): Promise<boolean> {
    // REST path (isDbMigrated=true): changing the reference brand at order level requires
    // regrouping because reference_brand_code is part of the group key. Run the three-step flow:
    // PATCH all articles -> POST /article-groups -> loadSizingRest().
    if (isDbMigrated()) {
      return this.bulkApplyReferenceBrandRest(referenceBrandCode);
    }

    const groups: ApplySizingGroupInput[] = this.sizingGroups
      .filter((group) => {
        if (referenceBrandCode) {
          return true;
        }
        if (!group.selectedSizeKey || !group.selectedSizeChart) {
          return false;
        }
        return group.sizing?.referenceBrandCode;
      })
      .map((group) => {
        const { commodityGroup, groupKey, selectedSizeKey, selectedSizeChart, sizingNote, sizeStructure } = group;
        if (!selectedSizeKey || !selectedSizeChart) {
          throw new Error('Filter error in bulkApplyReferenceBrand');
        }
        return {
          sizingGroup: {
            groupKey,
            commodityGroupCode: commodityGroup.code,
            preselectedSizeChartId: getPreselectedSizeChartId(group),
            sizeStructure,
            sizingNote,
          },
          sizeChartCode: selectedSizeChart.sizeChartCode,
          lengthChartCode: selectedSizeChart.lengthChartCode,
          sizeKeyId: selectedSizeKey.sizeKeyId,
          sizeKey: selectedSizeKey.sizeKey,
          manualSizeKey: selectedSizeKey.manualSizeKey,
          manualSizeList: selectedSizeKey.manualSizeKey ? selectedSizeKey.sizeList : [],
          manualLengthList: selectedSizeKey.manualSizeKey ? selectedSizeKey.lengthList : [],
          manualSizeLengthSeparator: selectedSizeKey.manualSizeKey ? selectedSizeKey.sizeLengthSeparator : undefined,
          distribution:
            selectedSizeKey.distributionType === DistributionType.MANUAL ? selectedSizeKey.distribution : undefined,
          resetManualDistribution: false,
          sizingNote,
          sizeStructure,
        };
      });
    const applySizingBulkResponse: ApplySizingBulkResponseUnion = await presizeBackend.applySizingBulk(
      this.referenceId,
      this.brandCode,
      this.seasonCode,
      groups,
      referenceBrandCode || null
    );
    if (!applySizingBulkResponse.applySizingBulk.ok) {
      message.error('No recommendation found, please use another reference brand', 5);
      return false;
    }
    await runInAction(async () => {
      const responseMap: Record<string, ApplySizingGroupResponse> = {};
      (applySizingBulkResponse as ApplySizingBulkResponse).applySizingBulk.results.forEach((response) => {
        responseMap[response.groupKey] = response;
      });
      const validGroups = this.sizingGroups.filter((group) => group.selectedSizeKey && responseMap[group.groupKey]);
      for (const group of validGroups) {
        const sizeDist = responseMap[group.groupKey];
        group.selectedSizeKey!.distribution = sizeDist.distribution;
        group.selectedSizeKey!.distributionType = sizeDist.distributionType;
        group.selectedSizeKey!.recommendationLevel = sizeDist.recoLevel;
        group.selectedSizeKey!.recommendationStrategy = sizeDist.recoStrategy;
        if (group.sizing) {
          group.sizing.referenceBrandCode = referenceBrandCode || null;
          group.reusedDecision = null;
        }
        const articlesWithChangedSizing = await this.calculateQuantities(false, group);
        if (articlesWithChangedSizing?.length) {
          sendEvent(group, 'applyReferenceBrand', articlesWithChangedSizing, this.additionalData());
        }
      }
      this.processReferenceBrandsWithUsage();

      // Notify host application about changed quantities
      sendSizingAppliedEventForGroups(this.sizingGroups);
    });
    return true;
  }

  /**
   * Order-level reference brand change/removal via REST (isDbMigrated=true).
   *
   * reference_brand_code is part of the group key, so changing it across all articles
   * invalidates existing group boundaries. Groups must be regenerated from scratch.
   *
   * Three-step flow:
   *   1. PATCH /api/orders/{order_code}/articles with ALL article reference IDs and the
   *      reference brand (null to remove) using Content-Type: application/merge-patch+json.
   *   2. POST /api/orders/{order_code}/article-groups to regenerate groups.
   *   3. loadSizingRest() re-triggers sizing for ALL groups (GET recommendation ->
   *      recalculate quantities -> BHOS), same as a first-time order open.
   *
   * Error handling:
   *   - PATCH fails -> do not proceed, do not update the UI (error surfaced by restApi).
   *   - PATCH ok but POST /article-groups fails -> prompt the user to reload.
   *   - Per-group sizing failures are handled inside loadSizingRest().
   */
  private async bulkApplyReferenceBrandRest(this: SizingStore, referenceBrandCode?: string | null): Promise<boolean> {
    // Step 1: PATCH all articles in the order.
    try {
      await presizeBackendRest.updateArticles(
        this.referenceId,
        {
          article_reference_ids: this.articles.map((article) => article.referenceId),
          reference_brand_code: referenceBrandCode ?? null,
        },
        undefined,
        'application/merge-patch+json'
      );
    } catch (error) {
      // PATCH failed: do not call POST /article-groups and do not update the UI.
      return false;
    }

    // Steps 2 & 3: regenerate groups and re-trigger sizing for all groups.
    try {
      await this.regroupAndReloadSizing();
    } catch (error) {
      message.error('Could not update the reference brand. Please reload the page and try again.', 5);
      return false;
    }

    return true;
  }
  async applyReferenceBrand(
    this: SizingStore,
    referenceBrandCode?: string | null,
    selectedGroupKey?: string
  ): Promise<boolean> {
    const selectedGroup = this.sizingGroups.find((group) => group.groupKey === selectedGroupKey);
    if (!selectedGroup) {
      throw new Error('Size group could not be found');
    }

    // REST path (isDbMigrated=true): PATCH this group's articles, then refresh sizing
    // for this one group only (PT-51855). GraphQL path below is unchanged when not migrated.
    if (isDbMigrated()) {
      return this.applyReferenceBrandRest(selectedGroup, referenceBrandCode);
    }

    const { selectedSizeKey, selectedSizeChart, sizingNote, sizeStructure, sizing } = selectedGroup;
    if (!selectedSizeKey || !selectedSizeChart) {
      return true;
    }
    const sizeDistribution = await presizeBackend.applySizing(
      this.referenceId,
      this.brandCode,
      this.seasonCode,
      selectedGroup,
      selectedSizeChart,
      selectedSizeKey,
      false,
      referenceBrandCode || null,
      sizeStructure,
      sizingNote
    );
    if (sizeDistribution.distributionType === DistributionType.UNKNOWN) {
      message.error('No recommendation found, please use another reference brand', 5);
      return false;
    }
    runInAction(async () => {
      selectedSizeKey.distribution = sizeDistribution.distribution;
      selectedSizeKey.distributionType = sizeDistribution.distributionType;
      selectedSizeKey.recommendationLevel = sizeDistribution.recoLevel;
      selectedSizeKey.recommendationStrategy = sizeDistribution.recoStrategy;
      if (sizing) {
        sizing.referenceBrandCode = referenceBrandCode || null;
        this.processReferenceBrandsWithUsage();
        selectedGroup.reusedDecision = null;
      }
      const articlesWithChangedSizing = await this.calculateQuantities(false, selectedGroup);
      if (articlesWithChangedSizing?.length) {
        await sendEvent(selectedGroup, 'applyReferenceBrand', articlesWithChangedSizing, this.additionalData());
      }
    });
    return true;
  }

  /**
   * Group-level reference brand change/removal via REST (isDbMigrated=true, PT-51855).
   *
   * Two-step flow (group only — no regrouping, order-level regrouping is PT-56195):
   *   1. PATCH /api/orders/{order_code}/articles with this group's article reference IDs and
   *      the reference brand (null to remove) using Content-Type: application/merge-patch+json.
   *   2. Refresh sizing for THIS group only via reloadSizingForGroup() (GET recommendation
   *      with the new reference brand -> recalculate quantities -> BHOS). loadSizingRest()
   *      is intentionally NOT used, as it would re-fetch every group.
   *
   * Error handling:
   *   - PATCH fails -> do not call GET, do not update the UI (error surfaced by restApi).
   *   - PATCH ok but GET fails -> error surfaced by restApi, BHOS not updated, returns false.
   */
  private async applyReferenceBrandRest(
    this: SizingStore,
    group: SizingGroup,
    referenceBrandCode?: string | null
  ): Promise<boolean> {
    // Step 1: PATCH this group's articles.
    try {
      await presizeBackendRest.updateArticles(
        this.referenceId,
        {
          article_reference_ids: group.articles.map((article) => article.referenceId),
          reference_brand_code: referenceBrandCode ?? null,
        },
        undefined,
        'application/merge-patch+json'
      );
    } catch (error) {
      // PATCH failed: do not refresh sizing and do not update the UI.
      return false;
    }

    // Persist the new reference brand on the group so the GET uses it.
    runInAction(() => {
      group.referenceBrandCode = referenceBrandCode || undefined;
      if (group.sizing) {
        group.sizing.referenceBrandCode = referenceBrandCode || null;
      }
      group.reusedDecision = null;
    });

    // Step 2: refresh sizing for this group only and recalculate quantities (saved to BHOS).
    let articlesWithChangedSizing: Awaited<ReturnType<SizingStore['calculateQuantities']>>;
    try {
      // initialCalculation=false: overwrite already-sized articles with the new
      // reference brand's distribution instead of only flagging manual edits.
      articlesWithChangedSizing = await this.reloadSizingForGroup(group, undefined, false);
    } catch (error) {
      return false;
    }

    runInAction(() => this.processReferenceBrandsWithUsage());

    if (articlesWithChangedSizing?.length) {
      sendEvent(group, 'applyReferenceBrand', articlesWithChangedSizing, this.additionalData());
    }
    return true;
  }
}
